import { contrastRatio } from '@/content/color'
import { roleDescriptions, roleNames, type RoleName, type Roles, type ThemePair } from '@/content/theme'

/** The color each role is read against when its contrast is reported. */
const pairing: Partial<Record<RoleName, [RoleName, RoleName]>> = {
  ink: ['ink', 'bg'],
  'ink-muted': ['ink-muted', 'surface'],
  surface: ['ink', 'surface'],
  raised: ['ink', 'raised'],
  bg: ['ink', 'bg'],
  primary: ['on-primary', 'primary'],
  'primary-hover': ['on-primary', 'primary-hover'],
  accent: ['on-accent', 'accent'],
  'accent-ink': ['accent-ink', 'surface'],
  link: ['link', 'surface'],
  'border-strong': ['border-strong', 'surface'],
  focus: ['focus', 'surface'],
  success: ['success', 'surface'],
  warning: ['warning', 'surface'],
  error: ['error', 'surface'],
  info: ['info', 'surface'],
}

function Cell({ roles, role }: { roles: Roles; role: RoleName }) {
  const pair = pairing[role]
  const ratio = pair ? contrastRatio(roles[pair[0]], roles[pair[1]]) : undefined
  return (
    <td>
      <span className="msn-palette__value">
        <span className="msn-palette__chip" style={{ background: roles[role] }} aria-hidden="true" />
        <code>{roles[role]}</code>
      </span>
      {ratio !== undefined && (
        <span className="msn-palette__ratio">
          {ratio}:1
          <span className="mds-visually-hidden">
            {' '}
            {pair![0]} on {pair![1]}
          </span>
        </span>
      )}
    </td>
  )
}

export default function PaletteTable({ theme }: { theme: ThemePair }) {
  return (
    <div className="doc-table-wrap" tabIndex={0}>
      <table className="doc-table msn-palette">
        <caption className="mds-visually-hidden">Color roles in light and dark, with contrast ratios</caption>
        <thead>
          <tr>
            <th scope="col">Role</th>
            <th scope="col">Light</th>
            <th scope="col">Dark</th>
          </tr>
        </thead>
        <tbody>
          {roleNames.map((role) => (
            <tr key={role}>
              <th scope="row">
                <span className="msn-palette__role">
                  <code>--mds-{role}</code>
                  <span className="msn-palette__desc">{roleDescriptions[role]}</span>
                </span>
              </th>
              <Cell roles={theme.light} role={role} />
              <Cell roles={theme.dark} role={role} />
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
